import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  message,
  Row,
  Spin,
  Tag,
} from "antd";
import React, { Component, Fragment, RefObject } from "react";
import {
  AddressInterface,
  APIError,
  AssociatedAddress,
  AssociatedEmail,
  AssociatedPhone,
  Company,
  Country,
  Customer,
  CustomerBackgroundInformation,
  EmailInterface,
  FormFieldConfig,
  PhoneInterface,
  UpdateCompanyAddressFnProps,
  UpdateCompanyEmailFnProps,
  UpdateCompanyPhoneFnProps,
  UpdateCustomerAddressFnProps,
  UpdateCustomerEmailFnProps,
  UpdateCustomerPhoneFnProps,
} from "../helpers/shared-interfaces";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import BaseForm from "./Form/BaseForm";

import { AxiosResponse } from "axios";
import DisplayTag from "../components/DisplayTag";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { FormInstance } from "antd/lib/form";
import { CREATE_ADDRESS, CREATE_EMAIL, CREATE_PHONE } from "../gql/general";
import { CREATE_CUSTOMER_BACKGROUND_INFORMATION, UPDATE_CUSTOMER_BACKGROUND_INFORMATION } from "../gql/customers";
import ModalCreator from "./ModalCreator";

const { Panel } = Collapse;

export enum Constants {
  PostalAddressCode = "0",
  PhysicalAddressCode = "1",
  Physical = "Physical",
  Postal = "Postal",
}

type UnionOfAssociatedEntities =
  | AssociatedAddress
  | AssociatedEmail
  | AssociatedPhone
  | CustomerBackgroundInformation

interface Props {
  editingState?: boolean;
  activeKey?: string;
  countries: Country[];
  addressList: AssociatedAddress[];
  emailList: AssociatedEmail[];
  phoneList: AssociatedPhone[];
  customerBackgroundInformation?: CustomerBackgroundInformation;
  setBackgroundInfo?: (backgroundInfo: CustomerBackgroundInformation) => void
  company?: {
    assignEmailToCompany: (emailId: string) => Promise<void>;
    assignAddressToCompany: (addressId: string) => Promise<void>;
    assignPhoneToCompany: (phoneId: string) => Promise<void>;
    unAssignEmailToCompany: (emailId: string) => void;
    unAssignAddressToCompany: (addressId: string) => void;
    unAssignPhoneToCompany: (phoneId: string) => void;
    company: Company;
    updateCompanyPhone: (args: UpdateCompanyPhoneFnProps) => Promise<void>;
    updateCompanyAddress: (args: UpdateCompanyAddressFnProps) => Promise<void>;
    updateCompanyEmail: (args: UpdateCompanyEmailFnProps) => Promise<void>;
    markEmailAsPrimary: (emailId: string) => void;
    markAddressAsPrimary: (addressId: string) => void;
    markPhoneAsPrimary: (phoneId: string) => void;
    reactivatePhone: (id: string) => void
    reactivateEmail: (id: string) => void
    reactivateAddress: (id: string) => void
  };
  customer?: {
    assignEmailToCustomer: (emailId: string) => Promise<void>;
    assignAddressToCustomer: (addressId: string) => Promise<void>;
    assignPhoneToCustomer: (phoneId: string) => Promise<void>;
    unAssignEmailToCustomer: (emailId: string) => void;
    unAssignAddressToCustomer: (addressId: string) => void;
    unAssignPhoneToCustomer: (phoneId: string) => void;
    markEmailAsPrimary: (emailId: string) => void;
    markAddressAsPrimary: (addressId: string) => void;
    markPhoneAsPrimary: (phoneId: string) => void;
    updateCustomerEmail: (args: UpdateCustomerEmailFnProps) => Promise<void>;
    updateCustomerPhone: (args: UpdateCustomerPhoneFnProps) => Promise<void>;
    updateCustomerAddress: (
      args: UpdateCustomerAddressFnProps,
    ) => Promise<void>;
    customer: Customer;
    reactivatePhone: (id: string) => void
    reactivateEmail: (id: string) => void
    reactivateAddress: (id: string) => void
  };
}

interface State {
  isLoading: boolean;
  showDialog: boolean;
  modalTarget: null | string;
  idToBeDeleted: null | string;
  phoneBeingEdited?: AssociatedPhone;
  emailBeingEdited?: AssociatedEmail;
  addressBeingEdited?: AssociatedAddress;
  backgroundInfoBeingEdited?: CustomerBackgroundInformation;
  isCreatingAssociatedEntity: boolean;
  addressType: string;
  fatherDeceased?: boolean;
  motherDeceased?: boolean;
}

export function ModalToggler({
  modalTarget,
  showModal,
  deleteAddress,
  deleteEmail,
  deletePhone,
  toggleModal,
}: {
  modalTarget: string | null;
  showModal: boolean;
  deleteAddress: () => void;
  deleteEmail: () => void;
  deletePhone: () => void;
  toggleModal: () => void;
}): JSX.Element {
  switch (modalTarget) {
    case "email":
      return ModalCreator({
        showModal,
        onOkay: deleteEmail,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this email?",
      });
    case "address":
      return ModalCreator({
        showModal,
        onOkay: deleteAddress,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this address?",
      });
    case "phone":
      return ModalCreator({
        showModal,
        onOkay: deletePhone,
        toggleModal: toggleModal,
        inscription: "Are you sure you want to delete this phone?",
      });
    default:
      return <div></div>;
  }
}

export function CardCreator({
  id,
  isPrimary,
  leftHandSideUI,
  deleteBtn,
  markAsPrimaryBtn,
  editButton,
  editingState,
  isActive,
  inactiveTag,
  reactivateBtn
}: {
  id: string;
  isPrimary: boolean;
  leftHandSideUI: JSX.Element;
  deleteBtn?: JSX.Element;
  markAsPrimaryBtn?: JSX.Element;
  editButton?: JSX.Element;
  editingState?: boolean;
  isActive?: boolean;
  inactiveTag?: JSX.Element;
  reactivateBtn?: JSX.Element;
}): JSX.Element {
  const isActiveValue = isActive !== false;
  return (
    <Fragment key={id}>
      <Card
        className="associated-entity-card"
        style={{ marginBottom: "10px" }}
        key={id}
      >
        <Row>
          <Col
            span={11}
            style={{
              marginTop: "11px",
            }}
          >
            {leftHandSideUI}
          </Col>
          <Col
            span={13}
            style={{
              textAlign: "right",
            }}
          >
            {isActiveValue && isPrimary && <DisplayTag />}
            {isActiveValue && !isPrimary && markAsPrimaryBtn}
            {!isActiveValue && inactiveTag}
            {!isActiveValue && reactivateBtn}
            {isActiveValue && editingState && editButton}
            {isActiveValue && deleteBtn}
          </Col>
        </Row>
      </Card>
    </Fragment>
  );
}

export function EmailCardCreator({
  associatedEmail,
  toggleDeleteConfirmationModal,
  markEmailAsPrimary,
  setEntityToBeDeleted,
  setIdToBeDeleted,
  setCurrentlyEditing,
  editingState,
  reactivateEmail
}: {
  associatedEmail: AssociatedEmail;
  markEmailAsPrimary: (emailId: string) => void;
  toggleDeleteConfirmationModal: () => void;
  setEntityToBeDeleted: (entity: string) => void;
  setIdToBeDeleted: (id: string) => void;
  setCurrentlyEditing?: (
    type: string,
    object: UnionOfAssociatedEntities,
  ) => void;
  editingState?: boolean;
  reactivateEmail: (id: string) => void
}): JSX.Element {
  return CardCreator({
    id: associatedEmail.email.id,
    isPrimary: associatedEmail.isPrimary,
    leftHandSideUI: (
      <div>
        <p className="associated-entity-paragraph">
          {associatedEmail.email.address}
        </p>
      </div>
    ),
    markAsPrimaryBtn: (
      <Button
        id="email-card-mark-as-primary-btn"
        type="primary"
        style={{
          marginRight: "8px",
        }}
        onClick={(): void => {
          markEmailAsPrimary(associatedEmail.id);
        }}
      >
        Set as Primary
      </Button>
    ),
    deleteBtn: (
      <Button
        id="email-card-delete-btn"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          setEntityToBeDeleted("email");
          setIdToBeDeleted(associatedEmail.email.id);
          toggleDeleteConfirmationModal();
        }}
      >
        <DeleteOutlined /> Delete
      </Button>
    ),
    editButton: (
      <Button
        id="email-card-edit-btn"
        type="dashed"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          setCurrentlyEditing && setCurrentlyEditing("email", associatedEmail);
        }}
      >
        <EditOutlined /> Edit
      </Button>
    ),
    editingState,
    isActive: associatedEmail.isActive,
    inactiveTag: (
      <Tag
        color="#002c6d"
        style={{
          marginBottom: "2px",
        }}
      >
        Inactive
      </Tag>
    ),
    reactivateBtn: (
      <Button
        id="emailcard-reactivate-btn"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          reactivateEmail(associatedEmail?.email?.id);
        }}
      >
        Reactivate
      </Button>
    ),
  });
}

export function AddressCardCreator({
  associatedAddress,
  toggleDeleteConfirmationModal,
  markAddressAsPrimary,
  setEntityToBeDeleted,
  setIdToBeDeleted,
  setCurrentlyEditing,
  editingState,
  reactivateAddress
}: {
  associatedAddress: AssociatedAddress;
  toggleDeleteConfirmationModal: () => void;
  markAddressAsPrimary: (addressId: string) => void;
  setEntityToBeDeleted: (entity: string) => void;
  setIdToBeDeleted: (id: string) => void;
  setCurrentlyEditing?: (
    type: string,
    object: UnionOfAssociatedEntities
  ) => void;
  editingState?: boolean;
  reactivateAddress: (id: string) => void
}): JSX.Element {
  return (
    <CardCreator
      id={associatedAddress.address.id}
      isPrimary={associatedAddress.isPrimary}
      leftHandSideUI={
        <div>
          <p className="associated-entity-paragraph">
            {associatedAddress.address.streetName}
          </p>
          <p className="associated-entity-paragraph">
            {associatedAddress.address.poBox}
          </p>
          {associatedAddress.address.ghPost && (
            <p className="associated-entity-paragraph">
              {associatedAddress.address.ghPost}
            </p>
          )}
          <p className="associated-entity-paragraph">
            {associatedAddress.address.city}
          </p>
          <p className="associated-entity-paragraph">
            {associatedAddress.address.region}
          </p>
          <p className="associated-entity-paragraph">
            {associatedAddress.address.country.name}
          </p>
        </div>
      }
      markAsPrimaryBtn={
        <Button
          id="address-card-mark-as-primary-btn"
          type="primary"
          style={{ marginRight: "8px" }}
          onClick={(): void => {
            markAddressAsPrimary(associatedAddress.id);
          }}
        >
          Set as Primary
        </Button>
      }
      deleteBtn={
        <Button
          id="address-card-delete-btn"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            setEntityToBeDeleted("address");
            setIdToBeDeleted(associatedAddress.address.id);
            toggleDeleteConfirmationModal();
          }}
        >
          <DeleteOutlined /> Delete
        </Button>
      }
      editButton={
        <Button
          id="address-card-edit-btn"
          type="dashed"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            setCurrentlyEditing &&
              setCurrentlyEditing("address", associatedAddress);
          }}
        >
          <EditOutlined /> Edit
        </Button>
      }
      editingState={editingState}
      isActive={associatedAddress.isActive}
      inactiveTag={
        <Tag
          color="#002c6d"
          style={{ marginBottom: "2px" }}
        >
          Inactive
        </Tag>
      }
      reactivateBtn={
        <Button
          id="address-card-reactivate-btn"
          style={{ marginRight: "9px", marginTop: "6px" }}
          onClick={(): void => {
            reactivateAddress(associatedAddress?.address?.id);
          }}
        >
          Reactivate
        </Button>
      }
    />
  );
}

export function PhoneCardCreator({
  associatedPhone,
  toggleDeleteConfirmationModal,
  setEntityToBeDeleted,
  setIdToBeDeleted,
  markPhoneAsPrimary,
  setCurrentlyEditing,
  editingState,
  reactivatePhone
}: {
  associatedPhone: AssociatedPhone;
  toggleDeleteConfirmationModal: () => void;
  markPhoneAsPrimary: (phoneId: string) => void;
  setEntityToBeDeleted: (entity: string) => void;
  setIdToBeDeleted: (id: string) => void;
  setCurrentlyEditing?: (
    type: string,
    object: UnionOfAssociatedEntities,
  ) => void;
  editingState?: boolean;
  reactivatePhone: (id: string) => void
}): JSX.Element {
  return CardCreator({
    id: associatedPhone.phone.id,
    isPrimary: associatedPhone.isPrimary,
    leftHandSideUI: (
      <div>
        <p className="associated-entity-paragraph">
          <DisplayTag type={associatedPhone.phone.type} />
          {associatedPhone.phone.number}
        </p>
      </div>
    ),
    markAsPrimaryBtn: (
      <Button
        id="phone-card-mark-as-primary-btn"
        type="primary"
        style={{
          marginRight: "8px",
        }}
        onClick={(): void => {
          markPhoneAsPrimary(associatedPhone.id);
        }}
      >
        Set as Primary
      </Button>
    ),
    deleteBtn: (
      <Button
        id="phone-card-delete-btn"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          setEntityToBeDeleted("phone");
          setIdToBeDeleted(associatedPhone.phone.id);
          toggleDeleteConfirmationModal();
        }}
      >
        <DeleteOutlined /> Delete
      </Button>
    ),
    editButton: (
      <Button
        id="phone-card-edit-btn"
        type="dashed"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          setCurrentlyEditing && setCurrentlyEditing("phone", associatedPhone);
        }}
      >
        <EditOutlined /> Edit
      </Button>
    ),
    editingState,
    isActive: associatedPhone.isActive,
    inactiveTag: (
      <Tag
        color="#002c6d"
        style={{
          marginBottom: "2px",
        }}
      >
        Inactive
      </Tag>
    ),
    reactivateBtn: (
      <Button
        id="phone-card-reactivate-btn"
        style={{ marginRight: "9px", marginTop: "6px" }}
        onClick={(): void => {
          reactivatePhone(associatedPhone?.phone?.id);
        }}
      >
        Reactivate
      </Button>
    ),
  });
}

export function CustomerBackgroundInformationCardCreator({
  backgroundInfo,
  setCurrentlyEditing,
  editingState,
}: {
  backgroundInfo: CustomerBackgroundInformation;
  setCurrentlyEditing?: (type: string, object: UnionOfAssociatedEntities) => void;
  editingState?: boolean;
}): JSX.Element {
  return CardCreator({
    id: "background-info-card",
    isPrimary: false,
    leftHandSideUI: (
      <div className="associated-entity-paragraph" style={{ lineHeight: 1.9, fontSize: 15 }}>
        {backgroundInfo.fatherName && (
          <p>
            <strong>Father’s Name:</strong> {backgroundInfo.fatherName}
          </p>
        )}
        {backgroundInfo.fatherDeceased !== undefined && (
          <p>
            <strong>Father Deceased:</strong>{" "}
            {backgroundInfo.fatherDeceased ? "Yes" : "No"}
          </p>
        )}
        {backgroundInfo.fatherPhoneNumber && (
          <p>
            <strong>Father’s Phone:</strong> {backgroundInfo.fatherPhoneNumber}
          </p>
        )}
        {backgroundInfo.motherName && (
          <p>
            <strong>Mother’s Name:</strong> {backgroundInfo.motherName}
          </p>
        )}
        {backgroundInfo.motherDeceased !== undefined && (
          <p>
            <strong>Mother Deceased:</strong>{" "}
            {backgroundInfo.motherDeceased ? "Yes" : "No"}
          </p>
        )}
        {backgroundInfo.motherPhoneNumber && (
          <p>
            <strong>Mother’s Phone:</strong> {backgroundInfo.motherPhoneNumber}
          </p>
        )}
        {backgroundInfo.previousSsnitNumber && (
          <p>
            <strong>Previous SSNIT Number:</strong> {backgroundInfo.previousSsnitNumber}
          </p>
        )}
        {backgroundInfo.hometown && (
          <p>
            <strong>Hometown:</strong> {backgroundInfo.hometown}
          </p>
        )}
        {backgroundInfo.nextOfKinName && (
          <p>
            <strong>Next of Kin Name:</strong> {backgroundInfo.nextOfKinName}
          </p>
        )}
        {backgroundInfo.nextOfKinPhoneNumber && (
          <p>
            <strong>Next of Kin Phone:</strong> {backgroundInfo.nextOfKinPhoneNumber}
          </p>
        )}
        {backgroundInfo.previousEmployer && (
          <p>
            <strong>Previous Employer:</strong> {backgroundInfo.previousEmployer}
          </p>
        )}
        {Object.values(backgroundInfo).every(
          (v) => v === undefined || v === "" || v === null
        ) && <p style={{ color: "#aaa" }}>No background information provided.</p>}
      </div>
    ),
    editButton:
      setCurrentlyEditing && (
        <Button
          id="background-info-edit-btn"
          type="dashed"
          style={{ marginRight: 9, marginTop: 6 }}
          onClick={() => setCurrentlyEditing("backgroundInfo", backgroundInfo)}
        >
          <EditOutlined /> Edit
        </Button>
      ),
    deleteBtn: undefined,
    markAsPrimaryBtn: undefined,
    isActive: true,
    editingState,
    inactiveTag: undefined,
    reactivateBtn: undefined,
  });
}

export function AssociatedEntityListCreator({
  toggleDeleteConfirmationModal,
  setIdToBeDeleted,
  setEntityToBeDeleted,
  markPhoneAsPrimary,
  markAddressAsPrimary,
  markEmailAsPrimary,
  setCurrentlyEditing,
  editingState,
  reactivateEmail,
  reactivatePhone,
  reactivateAddress,
}: {
  toggleDeleteConfirmationModal: () => void;
  setIdToBeDeleted: (id: string) => void;
  setEntityToBeDeleted: (id: string) => void;
  markEmailAsPrimary: (emailId: string) => void;
  markAddressAsPrimary: (addressId: string) => void;
  markPhoneAsPrimary: (phoneId: string) => void;
  setCurrentlyEditing: (type: string, object: UnionOfAssociatedEntities) => void;
  editingState?: boolean;
  reactivateEmail: (id: string) => void;
  reactivatePhone: (id: string) => void;
  reactivateAddress: (id: string) => void;
  currentlyEditingEntity?: string | null;
  currentlyEditingObject?: UnionOfAssociatedEntities | null;
}): ({
  entity,
  array,
}: {
  entity: string;
  array:
  | AssociatedEmail[]
  | AssociatedAddress[]
  | AssociatedPhone[]
  | CustomerBackgroundInformation[];
}) => JSX.Element[] {
  return ({
    entity,
    array,
  }): JSX.Element[] => {
    const elements: JSX.Element[] = [];

    switch (entity) {
      case "address":
        for (const item of array as AssociatedAddress[]) {
          elements.push(
            AddressCardCreator({
              markAddressAsPrimary,
              associatedAddress: item,
              toggleDeleteConfirmationModal,
              setIdToBeDeleted,
              setEntityToBeDeleted,
              setCurrentlyEditing,
              editingState,
              reactivateAddress,
            }),
          );
        }
        break;

      case "email":
        for (const item of array as AssociatedEmail[]) {
          elements.push(
            EmailCardCreator({
              markEmailAsPrimary,
              associatedEmail: item,
              toggleDeleteConfirmationModal,
              setIdToBeDeleted,
              setEntityToBeDeleted,
              setCurrentlyEditing,
              editingState,
              reactivateEmail,
            }),
          );
        }
        break;

      case "phone":
        for (const item of array as AssociatedPhone[]) {
          elements.push(
            PhoneCardCreator({
              markPhoneAsPrimary,
              associatedPhone: item,
              toggleDeleteConfirmationModal,
              setIdToBeDeleted,
              setEntityToBeDeleted,
              setCurrentlyEditing,
              editingState,
              reactivatePhone,
            }),
          );
        }
        break;
      case "backgroundInfo":
        for (const item of array as CustomerBackgroundInformation[]) {
          elements.push(
            CustomerBackgroundInformationCardCreator({
              backgroundInfo: item,
              setCurrentlyEditing,
              editingState,
            }),
          );
        }
        break;

      default:
        elements.push(<div key="default_div" />);
        break;
    }

    return elements;
  };
}

export function generateAddressTypeCode(isPostalBox: boolean): string {
  let localInitialValue;
  isPostalBox ? (localInitialValue = "0") : (localInitialValue = "1");
  return localInitialValue;
}

export function createAddressFields({
  countries,
  createAddress,
  initialValue,
  changeHandler,
  addressType,
  cancelEdit,
}: {
  changeHandler: (value: string) => void;
  countries: Country[];
  createAddress: () => void;
  initialValue?: AssociatedAddress;
  addressType: string;
  cancelEdit?: () => void;
}): FormFieldConfig[] {
  const isPostalBox =
    (initialValue && initialValue.address.isPostalBox) || false;
  const localInitialValue = generateAddressTypeCode(isPostalBox);
  const addressTypeValue = addressType;

  const fields: FormFieldConfig[] = [
    {
      id: "addressType",
      name: "addressType",
      inputType: "select",
      label: "Address Type",
      rule: "string",
      placeholder: "Address Type",
      changeHandler: changeHandler,
      required: true,
      colConfig: {
        span: 23,
      },
      initialValue: localInitialValue,
      selectOptions: [
        { id: "0", name: Constants.Postal },
        { id: "1", name: Constants.Physical },
      ],
    },
    {
      id: "ghPost",
      name: "ghPost",
      inputType: "string",
      rule: "string",
      label: "Digital Address (GhanaPostGPS Code)",
      initialValue: initialValue?.address.ghPost,
      placeholder: "Enter address code here ",
      required: false,
      colConfig: {
        span: 23,
      },
    },
    {
      id: "streetName",
      name: "streetName",
      inputType: "string",
      rule: "string",
      label: "Street Name",
      initialValue: initialValue?.address.streetName,
      placeholder: "Enter your street name",
      required:
        addressTypeValue === Constants.PhysicalAddressCode ? true : false,
      colConfig: {
        span: 23,
      },
    },
    {
      id: "poBox",
      name: "poBox",
      inputType: "string",
      rule: "string",
      label: "Box Address",
      initialValue: initialValue?.address.poBox,

      placeholder: "Add your box address here",
      required: true,
      colConfig: {
        span: 23,
      },
    },
    {
      id: "city",
      name: "city",
      inputType: "string",
      rule: "string",
      label: "City",
      initialValue: initialValue?.address.city,
      placeholder: "Enter your City",
      required: true,
      colConfig: {
        span: 23,
      },
    },
    {
      id: "region",
      name: "region",
      inputType: "string",
      rule: "string",
      label: "Region",
      initialValue: initialValue?.address.region,
      placeholder: "Enter your region",
      required: true,
      colConfig: {
        span: 23,
      },
    },
    {
      id: "addressCountry",
      name: "addressCountry",
      inputType: "select",
      label: "Country",
      initialValue: initialValue?.address.country.id,

      rule: "string",
      placeholder: "Country",
      required: true,
      colConfig: {
        span: 23,
      },
      selectOptions: countries,
    },
  ];

  if (initialValue && cancelEdit) {
    fields.push({
      id: "addressButtonGroup",
      name: "addressButtonGroup",
      inputType: "buttonGroup",
      required: false,
      elementStyle: {
        position: "relative",
        top: "-3px",
      },
      buttonGroup: [
        {
          label: "Update Address",
          className: "base-button",
          onClick: createAddress,
        },
        {
          label: "Cancel Edit",
          className: "warning-btn",
          onClick: cancelEdit,
        },
      ],
      colConfig: {
        span: 23,
      },
    });
  } else {
    fields.push({
      id: "createAddressButton",
      name: "createAddressButton",
      inputType: "button",
      value: initialValue ? "Update Address" : "Save Address",
      required: true,
      className: "base-button",
      elementStyle: {
        position: "relative",
        top: "-3px",
      },
      handleClick: createAddress,
      colConfig: {
        span: 23,
      },
    });
  }

  return fields;
}

export function createEmailFields({
  createEmail,
  initialValue,
  cancelEdit,
}: {
  createEmail: () => void;
  initialValue?: AssociatedEmail;
  cancelEdit?: () => void;
}): FormFieldConfig[] {
  const fields: FormFieldConfig[] = [
    {
      id: "email",
      name: "email",
      inputType: "string",
      rule: "email",
      label: "Email",
      placeholder: "Email Address",
      initialValue: initialValue?.email.address,
      required: false,
      colConfig: {
        span: 23,
      },
    },
  ];

  if (initialValue && cancelEdit) {
    fields.push({
      id: "emailButtonGroup",
      name: "emailButtonGroup",
      inputType: "buttonGroup",
      required: false,
      elementStyle: {
        position: "relative",
        top: "-3px",
      },
      buttonGroup: [
        {
          label: "Update Email",
          className: "base-button",
          onClick: createEmail,
        },
        {
          label: "Cancel Edit",
          className: "warning-btn",
          onClick: cancelEdit,
        },
      ],
      colConfig: {
        span: 23,
      },
    });
  } else {
    fields.push({
      id: "createEmailButton",
      name: "createEmailButton",
      inputType: "button",
      value: initialValue ? "Update Email" : "Save Email",
      required: false,
      className: "base-button",
      elementStyle: {
        position: "relative",
        top: "-3px",
      },
      handleClick: createEmail,
      colConfig: {
        span: 23,
      },
    });
  }

  return fields;
}

export function createPhoneFields({
  createPhone,
  initialValue,
  cancelEdit,
}: {
  createPhone: () => void;
  initialValue?: AssociatedPhone;
  cancelEdit?: () => void;
}): FormFieldConfig[] {
  const fields: FormFieldConfig[] = [
    {
      id: "phone",
      name: "phone",
      inputType: "phone",
      rule: "string",
      label: "Phone",
      placeholder: "Phone",
      required: false,
      initialValue: initialValue?.phone.number,
      colConfig: {
        span: 23,
      },
    },
  ];

  if (initialValue && cancelEdit) {
    fields.push({
      id: "phoneButtonGroup",
      name: "phoneButtonGroup",
      inputType: "buttonGroup",
      required: false,
      elementStyle: {
        position: "relative",
        top: "-3px",
      },
      buttonGroup: [
        {
          label: "Update Phone Number",
          className: "base-button",
          onClick: createPhone,
        },
        {
          label: "Cancel Edit",
          className: "warning-btn",
          onClick: cancelEdit,
        },
      ],
      colConfig: {
        span: 23,
      },
    });
  } else {
    fields.push({
      id: "createPhoneButton",
      name: "createPhoneButton",
      inputType: "button",
      value: initialValue ? "Update Phone Number" : "Save Phone Number",
      required: true,
      className: "base-button",
      elementStyle: {
        position: "relative",
        top: "-3px",
      },
      handleClick: createPhone,
      colConfig: {
        span: 23,
      },
    });
  }

  return fields;
}

export function createBackgroundInfoFields({
  handleFatherDeceasedChange,
  handleMotherDeceasedChange,
  createBackgroundInfo,
  updateBackgroundInfo,
  initialValue,
  fatherDeceased,
  motherDeceased,
  cancelEdit,
}: {

  handleFatherDeceasedChange: (checked: boolean) => void;
  handleMotherDeceasedChange: (checked: boolean) => void;
  createBackgroundInfo: () => void;
  updateBackgroundInfo?: () => void;
  initialValue?: CustomerBackgroundInformation;
  fatherDeceased?: boolean;
  motherDeceased?: boolean;
  cancelEdit?: () => void;
}): FormFieldConfig[] {
  const fields: FormFieldConfig[] = [
    {
      id: "fatherName",
      name: "fatherName",
      inputType: "string",
      rule: "string",
      placeholder: "Name of Father",
      label: "Name of Father",
      initialValue: initialValue?.fatherName || undefined,
      required: false,
      colConfig: {
        span: 7,
      },
    },
    {
      id: "fatherDeceased",
      name: "fatherDeceased",
      inputType: "checkbox",
      rule: "boolean",
      label: "Father Deceased?",
      checkboxCheckedValue: typeof fatherDeceased === "boolean"
        ? fatherDeceased
        : !!initialValue?.fatherDeceased,
      required: false,
      elementStyle: { width: "100%" },
      colConfig: {
        offset: 1,
        span: 7,
      },
      checkboxChangeHandler: (e: any) => handleFatherDeceasedChange(e.target.checked),
    },
    {
      id: "fatherPhoneNumber",
      name: "fatherPhoneNumber",
      inputType: "phone",
      rule: "string",
      placeholder: "Father's Phone Number",
      label: "Father's Phone Number",
      initialValue: initialValue?.fatherPhoneNumber,
      required: false,
      disabled: fatherDeceased,
      elementStyle: { width: "100%" },
      colConfig: {
        offset: 1,
        span: 7,
      },
    },
    {
      id: "motherName",
      name: "motherName",
      inputType: "string",
      rule: "string",
      placeholder: "Name of Mother",
      label: "Name of Mother",
      initialValue: initialValue?.motherName || undefined,
      required: false,
      colConfig: {
        span: 7,
      },
    },
    {
      id: "motherDeceased",
      name: "motherDeceased",
      inputType: "checkbox",
      rule: "boolean",
      label: "Mother Deceased?",
      checkboxCheckedValue: typeof motherDeceased === "boolean"
        ? motherDeceased
        : !!initialValue?.motherDeceased,
      required: false,
      elementStyle: { width: "100%" },
      colConfig: {
        offset: 1,
        span: 7,
      },
      checkboxChangeHandler: (e: any) => handleMotherDeceasedChange(e.target.checked),
    },
    {
      id: "motherPhoneNumber",
      name: "motherPhoneNumber",
      inputType: "phone",
      rule: "string",
      placeholder: "Mother's Phone Number",
      label: "Mother's Phone Number",
      initialValue: initialValue?.motherPhoneNumber,
      required: false,
      disabled: motherDeceased,
      elementStyle: { width: "100%" },
      colConfig: {
        offset: 1,
        span: 7,
      },
    },
    {
      id: "nextOfKinName",
      name: "nextOfKinName",
      inputType: "string",
      rule: "string",
      placeholder: "Full Name",
      label: "Name of Next of Kin",
      initialValue: initialValue?.nextOfKinName,
      required: false,
      colConfig: {
        span: 7,
      },
    },
    {
      id: "nextOfKinPhoneNumber",
      name: "nextOfKinPhoneNumber",
      inputType: "phone",
      rule: "string",
      label: "Next Of Kin Phone Number",
      placeholder: "Next Of Kin Phone Number",
      required: false,
      initialValue: initialValue?.nextOfKinPhoneNumber,
      elementStyle: { width: "100%" },
      colConfig: {
        offset: 1,
        span: 7,
      },
    },
    {
      id: "previousEmployer",
      name: "previousEmployer",
      inputType: "string",
      rule: "string",
      placeholder: "Previous Employer",
      label: "Previous Employer",
      initialValue: initialValue?.previousEmployer,
      required: false,
      colConfig: {
        span: 7,
        offset: 1,
      },
    },
  ];

  if (initialValue && cancelEdit) {
    fields.push({
      id: "backgroundInfoButtonGroup",
      name: "backgroundInfoButtonGroup",
      inputType: "buttonGroup",
      required: false,
      elementStyle: {
        position: "relative",
      },
      buttonGroup: [
        {
          label: "Update Background Info",
          className: "base-button",
          onClick: updateBackgroundInfo,
        },
        {
          label: "Cancel Edit",
          className: "warning-btn",
          onClick: cancelEdit,
        },
      ],
      colConfig: {
        span: 23,
      },
    });
  } else {
    fields.push({
      id: "createCustomerBackgroundInformation",
      name: "createCustomerBackgroundInformation",
      inputType: "button",
      value: initialValue ? "Update Background Info" : "Save Background Info",
      required: false,
      className: "base-button",
      elementStyle: {
        position: "relative",
      },
      handleClick: initialValue ? updateBackgroundInfo : createBackgroundInfo,
      colConfig: {
        span: 23,
      },
    });
  }

  return fields;
}

export default class AssociatedEntitiesCreator extends Component<Props, State> {
  addressReference: RefObject<FormInstance>;
  emailReference: RefObject<FormInstance>;
  phoneReference: RefObject<FormInstance>;
  backgroundInfoReference: RefObject<FormInstance>;

  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: false,
      addressType: Constants.PhysicalAddressCode,
      isCreatingAssociatedEntity: false,
      showDialog: false,
      modalTarget: null,
      idToBeDeleted: null,
      phoneBeingEdited: undefined,
    };

    this.addressReference = React.createRef<FormInstance>();
    this.emailReference = React.createRef<FormInstance>();
    this.phoneReference = React.createRef<FormInstance>();
    this.backgroundInfoReference = React.createRef<FormInstance>();

    this.createAddress = this.createAddress.bind(this);
    this.createEmail = this.createEmail.bind(this);
    this.createPhone = this.createPhone.bind(this);
  }

  setRef = (
    name: "addressReference" | "emailReference" | "phoneReference" | "backgroundInfoReference",
    ref: React.RefObject<FormInstance>,
  ): void => {
    this[name] = ref;
  };

  toggleDeleteConfirmationModal = (): void => {
    const prevState = this.state.showDialog;
    this.setState({ showDialog: !prevState });
  };

  setEntityToBeDeleted = (modalTarget: string): void => {
    this.setState({ modalTarget });
  };

  cancelEdit = (type: "address" | "email" | "phone" | "backgroundInfo"): void => {
    if (type === "address") {
      this.setState({
        addressBeingEdited: undefined,
        addressType: Constants.PhysicalAddressCode,
      });
      this.addressReference.current?.resetFields();
      return;
    }
    if (type === "email") {
      this.setState({ emailBeingEdited: undefined });
      this.emailReference.current?.resetFields();
      return;
    }
    if (type === "phone") {
      this.setState({ phoneBeingEdited: undefined });
      this.phoneReference.current?.resetFields();
      return;
    }
    this.setState({
      backgroundInfoBeingEdited: undefined,
      fatherDeceased: false,
      motherDeceased: false,
    });
    this.backgroundInfoReference.current?.resetFields();
  };

  setIdToBeDeleted = (idToBeDeleted: string): void => {
    this.setState({ idToBeDeleted });
  };

  markEmailAsPrimary = (emailId: string): void => {
    this.props.customer && this.props.customer.markEmailAsPrimary(emailId);
    this.props.company && this.props.company.markEmailAsPrimary(emailId);
  };

  markAddressAsPrimary = (addressId: string): void => {
    this.props.customer && this.props.customer.markAddressAsPrimary(addressId);
    this.props.company && this.props.company.markAddressAsPrimary(addressId);
  };

  markPhoneAsPrimary = (phoneId: string): void => {
    this.props.customer && this.props.customer.markPhoneAsPrimary(phoneId);
    this.props.company && this.props.company.markPhoneAsPrimary(phoneId);
  };

  reactivatePhone = (id: string): void => {
    this.props.customer && this.props.customer.reactivatePhone(id);
    this.props.company && this.props.company.reactivatePhone(id);
  };

  reactivateEmail = (id: string): void => {
    this.props.customer && this.props.customer.reactivateEmail(id);
    this.props.company && this.props.company.reactivateEmail(id);
  };

  reactivateAddress = (id: string): void => {
    this.props.customer && this.props.customer.reactivateAddress(id);
    this.props.company && this.props.company.reactivateAddress(id);
  };

  updateOrAssignPhone = (phoneId: string | void): void => {
    const { phoneBeingEdited } = this.state;
    const { editingState } = this.props;

    if (!phoneId) {
      return;
    }

    if (phoneBeingEdited && editingState && this.props.customer) {
      const data: UpdateCustomerPhoneFnProps = {
        newlyCreatedPhoneId: phoneId as string,
        customerPhone: phoneBeingEdited as AssociatedPhone,
      };
      this.props.customer.updateCustomerPhone(data).then((): void => {
        this.setState({ phoneBeingEdited: undefined });
      });
    } else if (this.props.customer) {
      this.props.customer.assignPhoneToCustomer(phoneId);
    }

    if (phoneBeingEdited && editingState && this.props.company) {
      const data: UpdateCompanyPhoneFnProps = {
        newlyCreatedPhoneId: phoneId as string,
        companyPhone: phoneBeingEdited as AssociatedPhone,
      };
      this.props.company.updateCompanyPhone(data).then((): void => {
        this.setState({ phoneBeingEdited: undefined });
      });
    } else if (this.props.company) {
      this.props.company.assignPhoneToCompany(phoneId);
    }
  };

  updateOrAssignEmail = (emailId: string | void): void => {
    const { emailBeingEdited } = this.state;
    const { editingState } = this.props;

    if (!emailId) {
      return;
    }

    if (emailBeingEdited && editingState && this.props.customer) {
      const data: UpdateCustomerEmailFnProps = {
        newlyCreatedEmailId: emailId as string,
        customerEmail: emailBeingEdited as AssociatedEmail,
      };
      this.props.customer.updateCustomerEmail(data).then((): void => {
        this.setState({ emailBeingEdited: undefined });
      });
    } else if (this.props.customer) {
      this.props.customer.assignEmailToCustomer(emailId);
    }

    if (emailBeingEdited && editingState && this.props.company) {
      const data: UpdateCompanyEmailFnProps = {
        newlyCreatedEmailId: emailId as string,
        companyEmail: emailBeingEdited as AssociatedEmail,
      };
      this.props.company.updateCompanyEmail(data).then((): void => {
        this.setState({ emailBeingEdited: undefined });
      });
    } else if (this.props.company) {
      this.props.company.assignEmailToCompany(emailId);
    }
  };

  updateOrAssignAddress = (addressId: string | void): void => {
    const { addressBeingEdited } = this.state;
    const { editingState } = this.props;

    if (!addressId) {
      return;
    }

    if (addressBeingEdited && editingState && this.props.customer) {
      const data: UpdateCustomerAddressFnProps = {
        newlyCreatedAddressId: addressId as string,
        customerAddress: addressBeingEdited as AssociatedAddress,
      };
      this.props.customer.updateCustomerAddress(data).then((): void => {
        this.setState({ addressBeingEdited: undefined });
      });
    } else if (this.props.customer) {
      this.props.customer.assignAddressToCustomer(addressId);
    }

    if (addressBeingEdited && editingState && this.props.company) {
      const data: UpdateCompanyAddressFnProps = {
        newlyCreatedAddressId: addressId as string,
        companyAddress: addressBeingEdited as AssociatedAddress,
      };
      this.props.company.updateCompanyAddress(data).then((): void => {
        this.setState({ addressBeingEdited: undefined });
      });
    } else if (this.props.company) {
      this.props.company.assignAddressToCompany(addressId);
    }
  };

  async createPhone(): Promise<void> {
    const fieldsToBeValidated: string[] = ["phone"];

    interface Response {
      phone: PhoneInterface;
      errors: APIError[];
    }

    if (!this.phoneReference.current) return;

    let data;

    try {
      data = await this.phoneReference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, isCreatingAssociatedEntity: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_PHONE,
        variables: {
          number: data.phone,
        },
      });

      this.setState({
        isLoading: false,
        isCreatingAssociatedEntity: false,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createPhone"
      );

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.phoneReference.current.setFields(err.map(mapAPIErrorToFormError));
        message.error(generateDisplayErrorMessage(err));
      } else if (mutation.phone) {
        this.phoneReference.current.resetFields(fieldsToBeValidated);
        this.updateOrAssignPhone(mutation.phone.id);
      }
    } catch (error) {
      this.setState({
        isLoading: false,
        isCreatingAssociatedEntity: false,
      });

      message.error(`Phone Creation Failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  async createEmail(): Promise<void> {
    const fieldsToBeValidated: string[] = ["email"];

    interface Response {
      email: EmailInterface;
      errors: APIError[];
    }

    if (!this.emailReference.current) return;

    let data;

    try {
      data = await this.emailReference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, isCreatingAssociatedEntity: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_EMAIL,
        variables: {
          address: data.email,
        },
      });

      this.setState({
        isLoading: false,
        isCreatingAssociatedEntity: false,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createEmail"
      );

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.emailReference.current.setFields(err.map(mapAPIErrorToFormError));
        message.error(generateDisplayErrorMessage(err));
      } else if (mutation.email) {
        this.emailReference.current.resetFields(fieldsToBeValidated);
        this.updateOrAssignEmail(mutation.email.id);
      }
    } catch (error) {
      this.setState({
        isLoading: false,
        isCreatingAssociatedEntity: false,
      });
      Sentry.captureException(error);
    }
  }

  async createAddress(): Promise<void> {
    const fieldsToBeValidated: string[] = [
      "streetName",
      "poBox",
      "city",
      "addressCountry",
      "addressType",
      "ghPost",
      "region",
    ];

    interface Response {
      address: AddressInterface;
      errors: APIError[];
    }

    if (!this.addressReference.current) return;

    let data;

    try {
      data = await this.addressReference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, isCreatingAssociatedEntity: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_ADDRESS,
        variables: {
          streetName: data.streetName,
          poBox: data.poBox,
          city: data.city,
          region: data.region,
          addressCountry: data.addressCountry,
          isPostalBox: data.addressType === Constants.PostalAddressCode,
          ghPost: data.ghPost?.length > 0 ? data.ghPost : undefined,
        },
      });

      this.setState({
        isLoading: false,
        isCreatingAssociatedEntity: false,
      });

      const { err, handled, mutation } = responseHandler<Response>(
        response,
        "createAddress"
      );

      if (handled) {
        message.error("An Error Occurred");
        return;
      } else if (err) {
        this.addressReference.current.setFields(err.map(mapAPIErrorToFormError));
        message.error(generateDisplayErrorMessage(err));
      } else if (mutation.address) {
        this.addressReference.current.resetFields(fieldsToBeValidated);
        this.updateOrAssignAddress(mutation.address.id);
      }
    } catch (error) {
      this.setState({
        isLoading: false,
        isCreatingAssociatedEntity: false,
      });
    }
  }

  createCustomerBackgroundInformation = async (): Promise<void> => {
    const fieldsToBeValidated = [
      "fatherName",
      "fatherDeceased",
      "fatherPhoneNumber",
      "motherName",
      "motherDeceased",
      "motherPhoneNumber",
      "previousSsnitNumber",
      "hometown",
      "nextOfKinName",
      "nextOfKinPhoneNumber",
      "previousEmployer",
    ];

    if (!this.backgroundInfoReference.current) return;

    let data;

    try {
      data = await this.backgroundInfoReference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, isCreatingAssociatedEntity: true });

    try {
      const response: AxiosResponse = await request({
        query: CREATE_CUSTOMER_BACKGROUND_INFORMATION,
        variables: {
          ...data,
          customerId: this.props.customer?.customer.id,
          fatherDeceased: this.state.fatherDeceased || false,
          motherDeceased: this.state.motherDeceased || false,
        },
      });

      const { err, mutation } = responseHandler<{ customerBackgroundInformation: CustomerBackgroundInformation; errors: APIError[] }>(
        response,
        "createCustomerBackgroundInformation"
      );

      if (err) {
        this.backgroundInfoReference.current.setFields(
          err.map(mapAPIErrorToFormError)
        );
      } else if (mutation.customerBackgroundInformation) {
        message.success("Successfully added background information");
        this.props.setBackgroundInfo?.(mutation.customerBackgroundInformation);
        this.backgroundInfoReference.current.resetFields(fieldsToBeValidated);
      }
    } catch (error) {
      message.error(`Failed to create background information - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
    finally {
      this.setState({ isLoading: false, isCreatingAssociatedEntity: false });
    }
  };

  updateCustomerBackgroundInformation = async (): Promise<void> => {
    interface Response {
      customerBackgroundInformation: CustomerBackgroundInformation;
      errors: APIError[];
    }

    const fieldsToBeValidated = [
      "fatherName",
      "fatherDeceased",
      "fatherPhoneNumber",
      "motherName",
      "motherDeceased",
      "motherPhoneNumber",
      "previousSsnitNumber",
      "hometown",
      "nextOfKinName",
      "nextOfKinPhoneNumber",
      "previousEmployer",
    ];

    if (!this.backgroundInfoReference.current) return;

    let data;

    try {
      data = await this.backgroundInfoReference.current.validateFields(fieldsToBeValidated);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    this.setState({ isLoading: true, isCreatingAssociatedEntity: true });

    try {
      const response: AxiosResponse = await request({
        query: UPDATE_CUSTOMER_BACKGROUND_INFORMATION,
        variables: {
          ...data,
          customerId: this.props.customer?.customer.id,
          fatherDeceased: this.state.fatherDeceased || false,
          motherDeceased: this.state.motherDeceased || false,
        },
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "updateCustomerBackgroundInformation"
      );

      if (err) {
        this.backgroundInfoReference.current.setFields(
          err.map(mapAPIErrorToFormError)
        );
      } else if (mutation.customerBackgroundInformation) {
        message.success("Successfully updated background information");
        this.props.setBackgroundInfo?.(mutation.customerBackgroundInformation);
      }
    } catch (error) {
      message.error(`Failed to update background information - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isLoading: false, isCreatingAssociatedEntity: false });
    }
  };

  deleteAddress = (): void => {
    const addressToBeDeleted: string | null = this.state.idToBeDeleted;
    this.setState({ showDialog: false });
    this.props.company &&
      this.props.company.unAssignAddressToCompany(addressToBeDeleted as string);
    this.props.customer &&
      this.props.customer.unAssignAddressToCustomer(
        addressToBeDeleted as string,
      );
  };

  deleteEmail = (): void => {
    const emailToBeDeleted: string | null = this.state.idToBeDeleted;
    this.setState({ showDialog: false });
    this.props.company &&
      this.props.company.unAssignEmailToCompany(emailToBeDeleted as string);
    this.props.customer &&
      this.props.customer.unAssignEmailToCustomer(emailToBeDeleted as string);
  };

  deletePhone = (): void => {
    const phoneToBeDeleted: string | null = this.state.idToBeDeleted;
    this.setState({ showDialog: false });
    this.props.company &&
      this.props.company.unAssignPhoneToCompany(phoneToBeDeleted as string);
    this.props.customer &&
      this.props.customer.unAssignPhoneToCustomer(phoneToBeDeleted as string);
  };

  setCurrentlyEditing = (
    type: string,
    object: UnionOfAssociatedEntities,
  ): void => {
    if (type === "phone") {
      this.setState({ phoneBeingEdited: object as AssociatedPhone });
    } else if (type === "email") {
      this.setState({ emailBeingEdited: object as AssociatedEmail });
    } else if (type === "address") {
      this.setState({ addressBeingEdited: object as AssociatedAddress });
    } else {
      this.setState({ backgroundInfoBeingEdited: object as CustomerBackgroundInformation });
    }
  };

  handleAddressTypeChange = (addressType: string): void => {
    this.setState({ addressType });
  };

  handleFatherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ fatherDeceased: checkedValue });
  };

  handleMotherDeceasedChange = (checkedValue: boolean): void => {
    this.setState({ motherDeceased: checkedValue });
  };

  render(): JSX.Element {
    let addressFieldsToHide: string[] = [];
    const {
      showDialog,
      modalTarget,
      isCreatingAssociatedEntity,
      phoneBeingEdited,
      emailBeingEdited,
      addressBeingEdited,
      addressType,
      backgroundInfoBeingEdited,
      fatherDeceased,
      motherDeceased,
    } = this.state;
    const { countries, emailList, addressList, phoneList, editingState, customerBackgroundInformation } =
      this.props;

    const addressFields: FormFieldConfig[] = createAddressFields({
      countries,
      createAddress: this.createAddress,
      initialValue: addressBeingEdited as AssociatedAddress,
      changeHandler: this.handleAddressTypeChange,
      addressType: this.state.addressType,
      cancelEdit: () => this.cancelEdit("address"),
    });
    const emailFields: FormFieldConfig[] = createEmailFields({
      createEmail: this.createEmail,
      initialValue: emailBeingEdited as AssociatedEmail,
      cancelEdit: () => this.cancelEdit("email"),
    });
    const phoneFields: FormFieldConfig[] = createPhoneFields({
      createPhone: this.createPhone,
      initialValue: phoneBeingEdited as AssociatedPhone,
      cancelEdit: () => this.cancelEdit("phone"),
    });
    const backgroundInfoFields: FormFieldConfig[] = createBackgroundInfoFields({
      createBackgroundInfo: this.createCustomerBackgroundInformation,
      updateBackgroundInfo: this.updateCustomerBackgroundInformation,
      initialValue: backgroundInfoBeingEdited,
      handleFatherDeceasedChange: this.handleFatherDeceasedChange,
      handleMotherDeceasedChange: this.handleMotherDeceasedChange,
      fatherDeceased,
      motherDeceased,
      cancelEdit: () => this.cancelEdit("backgroundInfo"),
    })

    if (addressType === Constants.PostalAddressCode) {
      addressFieldsToHide = ["ghPost"];
    } else {
      addressFieldsToHide = ["poBox"];
    }

    const entityListCreator = AssociatedEntityListCreator({
      toggleDeleteConfirmationModal: this.toggleDeleteConfirmationModal,
      setEntityToBeDeleted: this.setEntityToBeDeleted,
      setIdToBeDeleted: this.setIdToBeDeleted,
      markPhoneAsPrimary: this.markPhoneAsPrimary,
      markEmailAsPrimary: this.markEmailAsPrimary,
      markAddressAsPrimary: this.markAddressAsPrimary,
      setCurrentlyEditing: this.setCurrentlyEditing,
      editingState,
      reactivateEmail: this.reactivateEmail,
      reactivatePhone: this.reactivatePhone,
      reactivateAddress: this.reactivateAddress,
    });

    return (
      <Fragment>
        {ModalToggler({
          showModal: showDialog,
          deleteAddress: this.deleteAddress,
          deleteEmail: this.deleteEmail,
          deletePhone: this.deletePhone,
          modalTarget: modalTarget,
          toggleModal: this.toggleDeleteConfirmationModal,
        })}
        <Row>
          {
            <div
              className="sub-form"
              style={{
                width: "100%",
              }}
            >
              <div className="email-telephone-address-form">
                <Collapse>
                  {["Address", "Email", "Phone", "Background Information"].map(
                    (item: string): JSX.Element => {
                      let form;
                      let listCreator;
                      let array = [];

                      if (item === "Address") {
                        form = (
                          <BaseForm
                            key={`address-form-${addressBeingEdited?.id ?? "new"}`}
                            layout="vertical"
                            reference={this.addressReference}
                            fieldsToHide={addressFieldsToHide}
                            fields={addressFields}
                          />
                        );
                        listCreator = entityListCreator({
                          entity: "address",
                          array: addressList,
                        });
                        array = addressList;
                      } else if (item === "Email") {
                        form = (
                          <BaseForm
                            key={`email-form-${emailBeingEdited?.id ?? "new"}`}
                            fieldsToHide={[]}
                            layout="vertical"
                            reference={this.emailReference}
                            fields={emailFields}
                          />
                        );
                        listCreator = entityListCreator({
                          entity: "email",
                          array: emailList,
                        });
                        array = emailList;
                      } else if (item === "Phone") {
                        form = (
                          <BaseForm
                            key={`phone-form-${phoneBeingEdited?.id ?? "new"}`}
                            fieldsToHide={[]}
                            layout="vertical"
                            reference={this.phoneReference}
                            fields={phoneFields}
                          />
                        );
                        listCreator = entityListCreator({
                          entity: "phone",
                          array: phoneList,
                        });
                        array = phoneList;
                      } else {
                        form = (
                          <BaseForm
                            fieldsToHide={[]}
                            layout="vertical"
                            reference={this.backgroundInfoReference}
                            fields={backgroundInfoFields}
                          />
                        );
                        listCreator = entityListCreator({
                          entity: "backgroundInfo",
                          array: customerBackgroundInformation
                            ? [customerBackgroundInformation]
                            : [],
                        })
                      }

                      return (
                        <Panel
                          header={
                            <span>
                              {item}{" "}
                              <Badge
                                count={array.length}
                                style={{
                                  backgroundColor: "#52c41a",
                                  marginLeft: "12px",
                                }}
                              />
                            </span>
                          }
                          key={item}
                        >
                          <Spin spinning={isCreatingAssociatedEntity}>
                            <Row>
                              <Col
                                span={11}
                                style={{
                                  paddingRight: "20px",
                                }}
                              >
                                {form}
                              </Col>

                              <Col offset={1} span={12}>
                                {listCreator}
                              </Col>
                            </Row>
                          </Spin>
                        </Panel>
                      );
                    },
                  )}
                  {this.props.children}
                </Collapse>
              </div>
            </div>
          }
        </Row>
      </Fragment>
    );
  }
}
